/*
 * Copyright (c) 2025-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React, {Component} from 'react';
import {Database} from "../APIClient.jsx";
import DisplayIcon from "../components/Icons/DisplayIcon.jsx";
import ElementStatus from "../components/ElementStatus.jsx";
import DisplayNodeConfig from "../components/DisplayNodeConfig.jsx";
import DisplayPlannedConfig from "../components/DisplayPlannedConfig.jsx";
import DisplayNodeState from "../components/DisplayNodeState.jsx";
import DisplayCommand from "../components/DisplayCommand.jsx";
import {Nodes} from "../Objects/Nodes.jsx";
import ButtonComponent from "../components/BasicInputs/ButtonComponent.jsx";
import DisplayStateHistory from "../components/DisplayStateHistory.jsx";
import DisplayNodeInstallLog from "../components/DisplayNodeInstallLog.jsx";
import NodeCharts from "../components/Node/NodeCharts.jsx";
import Tabs from "../components/Tabs/Tabs.jsx";
import Tab from "../components/Tabs/Tab.jsx";
import NodeLogs from "../components/Node/NodeLogs.jsx";
import {Socket} from "../Socket.js";
import LiveConsole from "../components/LiveConsole.jsx";
import NodeSettings from "../components/Node/NodeSettings.jsx";
import {Subscription} from "../Subscription.js";
import Strings from 'toolbox/strings';

class NodePage extends Component {
    constructor() {
        super();
        this.state = {
            nodeInfo: {},
            isLoaded: false,
            currentTab: 0,

        }

        this.sub = new Subscription()
    }

    async fetchData() {
        let {nodeId} = this.props
        let node = await Nodes(nodeId).getInfo()
        this.setState({nodeInfo: node ?? {}, isLoaded: true})

        this.sub.add(() => Database.table("Nodes", "app").onChange((change) => {
            let nodeInfo = this.state.nodeInfo
            nodeInfo = Socket.applyChange([nodeInfo], change)[0]
            this.setState({nodeInfo: nodeInfo})
        }, [{
            column: "indexKey",
            value: nodeId
        }]), "liveData")


    }

    componentDidMount() {
        this.fetchData()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.nodeId !== this.props.nodeId) {
            this.fetchData()
        }
    }

    componentWillUnmount() {
        this.sub.unmount()
    }

    openNodeURL() {
        let {nodeInfo} = this.state
        window.open("http://" + nodeInfo.nodeDomain, '_blank', 'noopener,noreferrer')
    }

    async updateNode() {
        let {nodeInfo} = this.state

        await Nodes(nodeInfo.indexKey).updateClient()
    }

    async placeCertificates() {
        let {nodeInfo} = this.state

        await Nodes(nodeInfo.indexKey).placeCertificates()
    }

    async removeNode() {
        let {nodeInfo} = this.state

        await Nodes(nodeInfo.indexKey).remove()
    }


    async runSetup() {
        let {nodeInfo} = this.state

        await Nodes(nodeInfo.indexKey).setup()
    }

    render() {
        let {nodeId} = this.props

        let {nodeInfo, isLoaded} = this.state


        if (!isLoaded) {
            return (<div>Loading node info. {nodeId}</div>)
        }

        if (Strings.isEmpty(nodeInfo.indexKey)) {
            return (<div>
                The node was not found. (nodeId: {nodeId})
            </div>)

        }

        return (
            <div>

                <div className=" bg-white rounded-2xl my-3 p-5">
                    <div className="mx-auto flex items-center justify-between gap-x-8 lg:mx-0">
                        <div className="flex items-center gap-x-6">
                            <div
                                className={"h-16 w-16 flex-none rounded-full ring-1 ring-gray-900/10 flex justify-center items-center z-30"}>
                                <DisplayIcon iconName={"Node"} className={"h-7 w-7"}/>


                            </div>

                            <h1>
                                <div
                                    className="text-sm leading-6 text-gray-500">{nodeInfo?.nodeDomain ?? "[Not Set]"}</div>
                                <div
                                    className="mt-1 text-base font-semibold leading-6 text-gray-900">{nodeInfo?.nodeName ?? "[Not Set]"}</div>
                            </h1>
                        </div>
                        <div className="flex items-center gap-x-3">
                            <ElementStatus name={"Nodes"} elementId={nodeInfo?.indexKey}/>
                        </div>
                    </div>
                    <div className="flex items-center gap-x-3 pt-4">

                        <ButtonComponent
                            text={"Pause Node"}
                            iconName={((nodeInfo?.steeringType ?? "auto") === "auto" ? "IoPlay" : "IoPause")}
                            onClick={async () => {
                                if ((nodeInfo?.steeringType ?? "auto") === "auto") {
                                    await Nodes(nodeInfo.indexKey).update({["steeringType"]: "manuel"})
                                } else {
                                    await Nodes(nodeInfo.indexKey).update({["steeringType"]: "auto"})
                                }
                            }}/>

                        <ButtonComponent
                            text={"Remove Node"}
                            onClick={() => {
                                return this.removeNode()

                            }}/>
                        <ButtonComponent
                            text={" Update Node"}
                            onClick={() => {
                                return this.updateNode()

                            }}/>
                        <ButtonComponent
                            text={"runSetup"}
                            onClick={() => {
                                return this.runSetup()


                            }}/>

                        <ButtonComponent
                            text={"Open Node URL"}
                            iconName={"IoEyeOutline"}
                            onClick={() => {
                                return this.openNodeURL()


                            }}/>


                    </div>

                </div>
                <Tabs activeKey={this.state.currentTab}
                      onSelect={(number) => this.setState({currentTab: number})} variant={'default'} isBox>
                    <Tab eventKey={0} title={"Übersicht"}>
                        <NodeLogs nodeId={nodeInfo.indexKey}/>
                    </Tab>
                    <Tab eventKey={1} title={"State"}>
                        <DisplayStateHistory name={"Nodes"} elementId={nodeInfo.indexKey}/>

                    </Tab>

                    <Tab eventKey={2} title={"Einstellungen"}>

                        <NodeSettings nodeId={nodeInfo.indexKey}/>


                    </Tab>

                    <Tab eventKey={3} title={"Resources"}>
                        <NodeCharts nodeId={nodeInfo.indexKey}/>

                    </Tab>
                    <Tab eventKey={4} title={"DEV"}>
                        <div className={"space-y-3"}>
                            <DisplayPlannedConfig nodeId={nodeInfo.indexKey}/>
                            <DisplayNodeConfig nodeId={nodeInfo.indexKey}/>
                            <DisplayNodeState nodeId={nodeInfo.indexKey}/>
                            <DisplayNodeInstallLog nodeId={nodeInfo.indexKey}/>

                            <DisplayCommand
                                command={`ssh -t -o StrictHostKeyChecking=no 'root@${nodeInfo.nodeIP}' -p 22 'cd "/home"; screen -ls; docker container list; eval "$SHELL -il"'`}/>
                        </div>

                    </Tab>

                    <Tab eventKey={5} title={"Debug"}>
                        <LiveConsole nodeId={nodeInfo.indexKey}/>


                    </Tab>
                </Tabs>


            </div>
        );
    }
}

NodePage.propTypes = {};

NodePage.defaultProps = {};


export default NodePage;
